import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import DashboardLayout from '../../../../commons/DashboardLayout/DashboardLayout';
import Loading from '../../../../commons/Loading/Loading';
import { formatRemainingTime } from '../../../../../utils/helpers/date/formatRemainingTime';
import Empty from '../../../../commons/Empty/Empty';
import { useMergedStatesFromFetchers } from '../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import { useConsultationSummaryCOM } from '../../../../../hooks/fetchers/useConsultationSummaryCOM/useConsultationSummaryCOM';
import Button from '../../../../designSystem/Button/Button';
import { downloadAttachment } from '../../CreateConsultation/CreateConsultForthStep/CreateConsultForthStep.query';

export default () => {
  const { consultationId } = useParams();

  const {
    data,
    isLoading: isConsultationSummaryLoading,
    error: consultationSummaryError,
  } = useConsultationSummaryCOM(parseInt(consultationId as string), {
    enabled: Boolean(consultationId),
  });

  const { isLoading, error } = useMergedStatesFromFetchers({
    loadings: [
      isConsultationSummaryLoading
    ],
    errors: [consultationSummaryError],
  });

  return (
    <>
      <Helmet>
        <title>Résumé de la consultation #{consultationId}</title>
      </Helmet>
      <DashboardLayout style={{ backgroundColor: 'var(--gray-1)' }}>
        {error ? (
          <Empty>{error.message}</Empty>
        ) : isLoading ? (
          <Loading />
        ) : (
          <div style={{ display: 'flex', flex: 1, alignSelf: 'normal' }}>
            <div className="dashboard-history">
              <div className="dashboard-history__item">
                <div className="dashboard-history__item-row">
                  <div className="dashboard-history__item-content">
                    <label className="dashboard-history__item-text-1">
                      Titre de la consultation
                    </label>
                    <span className="dashboard-history__item-text-3">{data?.infos.title}</span>
                  </div>
                  {data?.infos.ref && (
                    <div className="createConsultForthStep__item-content">
                      <label className="createConsultForthStep__item-text-1">Référence</label>
                      <span className="createConsultForthStep__item-text-2">
                        {data?.infos.ref}
                      </span>
                    </div>
                  )}
                </div>
                <div className="dashboard-history__item-row">
                  <div className="dashboard-history__item-content">
                    <label className="dashboard-history__item-text-1">Type de prestation</label>
                    <span className="dashboard-history__item-text-2">{data?.infos.type}</span>
                  </div>
                  <div className="dashboard-history__item-content">
                    <label className="dashboard-history__item-text-1">Domaine d'activité</label>
                    <span className="dashboard-history__item-text-2">
                      {data?.infos.fieldOfActivity}
                    </span>
                  </div>
                </div>
              </div>
              <div className="dashboard-history__item dashboard-history__item--right">
                <div className="dashboard-history__item-row">
                  <div className="dashboard-history__item-content">
                    <label className="dashboard-history__item-text-1">Début de l'offre</label>
                    <span className="dashboard-history__item-text-4">
                      {moment(data?.infos.start).format('dddd DD MMMM YYYY')}
                    </span>
                  </div>
                  <div className="dashboard-history__item-content">
                    <label className="dashboard-history__item-text-1">
                      Durée de la consultation
                    </label>
                    <span className="dashboard-history__item-text-2">
                      {moment(data?.infos.end).diff(data?.infos.start, 'days')} jours
                    </span>
                  </div>
                </div>
                <div className="dashboard-history__item-row">
                  <div className="dashboard-history__item-content">
                    <label className="dashboard-history__item-text-1">Fin de l'offre</label>
                    <span className="dashboard-history__item-text-4">
                      {moment(data?.infos.end).format('dddd DD MMMM YYYY')}
                    </span>
                  </div>
                  {data?.infos.start &&
                    data?.infos.end &&
                    (moment().isBefore(data.infos.start) ? (
                      <div className="dashboard-history__item-content">
                        <label className="dashboard-history__item-text-1">Débute dans</label>
                        <span className="dashboard-history__item-text-2">
                          {formatRemainingTime(data.infos.start)}
                        </span>
                      </div>
                    ) : (
                      <div className="dashboard-history__item-content">
                        <label className="dashboard-history__item-text-1">Temps restant</label>
                        <span className="dashboard-history__item-text-2">
                          {formatRemainingTime(
                            moment(data?.infos.end)
                              .add(18, 'hours')
                              .toDate()
                          )}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
              {Boolean(data && data?.prestations.length > 0) && (
                <div className="dashboard-history__item">
                  <div className="dashboard-history__item-row">
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">
                        Contenu de la consultation
                      </label>
                      {data?.prestations.map(({ quantity, description }, index) => (
                        <span className="dashboard-history__item-text-2" key={index}>
                          {index === 0 && `\n`}{' '}
                          {quantity && (
                            <>
                              <strong>{quantity}</strong>x
                            </>
                          )}{' '}
                          {description} {`\n\n`}
                        </span>
                      ))}
                      {data?.infos.further_description &&
                        <span className="createConsultForthStep__item-text-2">
                          {data.infos.further_description}
                        </span>
                      }
                    </div>
                  </div>
                </div>
              )}
              {Boolean(data?.infos.firstName) && (
                <div className="dashboard-history__item dashboard-history__item--right">
                  <label className="dashboard-history__item-text-5">Personne à contacter</label>
                  <div className="dashboard-history__item-row">
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">Nom</label>
                      <span className="dashboard-history__item-text-2">
                        {data?.infos.lastName}
                      </span>
                    </div>
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">Prénom</label>
                      <span className="dashboard-history__item-text-2">
                        {data?.infos.firstName}
                      </span>
                    </div>
                  </div>
                  <div className="dashboard-history__item-row">
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">Adresse email</label>
                      <span className="dashboard-history__item-text-2">{data?.infos.email}</span>
                    </div>
                    <div className="dashboard-history__item-content">
                      <label className="dashboard-history__item-text-1">
                        Numéro de téléphone
                      </label>
                      <span className="dashboard-history__item-text-2">{data?.infos.phone}</span>
                    </div>
                  </div>
                </div>
              )}
              {data?.infos.attachments &&
                <div className="createConsultForthStep__item" style={{ paddingTop: 0 }}>
                  {data.infos.attachments.split(',').map((file, index) => (
                    <Button
                      key={index}
                      style={{
                        alignSelf: 'unset',
                        textAlign: 'center',
                        borderRadius: 6,
                        marginBottom: 'var(--spacing-s)',
                        width: '100%',
                        paddingLeft: 0,
                        paddingRight: 0,
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        downloadAttachment(file)
                      }}
                    >
                      {`Pièce jointe n°${index + 1}`}
                    </Button>
                  ))}
                </div>
              }
            </div>
          </div>
        )}
      </DashboardLayout>
    </>
  );
};
